import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./SuperAdminDashboardPage.css";

/**
 * Super Admin Dashboard — production scope
 *
 * Purpose:
 * - High-level governance and safety overview for Super Admins
 * - Anchor page for platform oversight
 *
 * Current scope:
 * - Reads from the Super Admin system health endpoint
 * - No billing, provider repair, or advanced analytics on this dashboard
 * - No PHI display
 *
 * Dashboard focuses on:
 * - Pending approvals
 * - Clinic governance
 * - Public listing safety
 * - Stuck patient-request workflow signals
 * - Audit/activity visibility
 *
 * Endpoint:
 * - GET /api/super-admin/system-health
 */

type DashboardTone = "neutral" | "action" | "warning";

type HealthSummary = {
  pendingApprovals: number;
  oldPendingApprovals: number;
  approvedClinics: number;
  clinicsWithoutAdmin: number;
  publicListingIssues: number;
  stuckRequests: number;
  auditEventsToday: number;
};

type ClinicSafetyItem = {
  clinicId: number;
  clinicName: string | null;
  governanceStatus?: string | null;
  approvedAt?: string | null;
  cityName?: string | null;
  postalCode?: string | null;
};

type PublicListingIssueItem = ClinicSafetyItem & {
  missing?: string[];
};

type OldPendingApprovalItem = {
  accessRequestId: number;
  userId: number | null;
  requestedRole: string | null;
  clinicAdminAction: string | null;
  submittedAt: string | null;
  clinicId: number | null;
  existingClinicName: string | null;
  newClinicName: string | null;
};

type StuckRequestItem = {
  requestId: number;
  clinicId: number | null;
  clinicName: string | null;
  familyDoctorProfileId: number | null;
  statusName: string | null;
  submittedAt: string | null;
  verifiedAt: string | null;
};

type HealthResponse = {
  summary?: Partial<HealthSummary>;
  lists?: {
    clinicsWithoutAdmin?: ClinicSafetyItem[];
    publicListingIssues?: PublicListingIssueItem[];
    oldPendingApprovals?: OldPendingApprovalItem[];
    stuckRequests?: StuckRequestItem[];
  };
  error?: string;
};

const EMPTY_SUMMARY: HealthSummary = {
  pendingApprovals: 0,
  oldPendingApprovals: 0,
  approvedClinics: 0,
  clinicsWithoutAdmin: 0,
  publicListingIssues: 0,
  stuckRequests: 0,
  auditEventsToday: 0,
};

export default function SuperAdminDashboardPage() {
  const [summary, setSummary] = useState<HealthSummary>(EMPTY_SUMMARY);
  const [clinicsWithoutAdmin, setClinicsWithoutAdmin] = useState<
    ClinicSafetyItem[]
  >([]);
  const [publicListingIssues, setPublicListingIssues] = useState<
    PublicListingIssueItem[]
  >([]);
  const [oldPendingApprovals, setOldPendingApprovals] = useState<
    OldPendingApprovalItem[]
  >([]);
  const [stuckRequests, setStuckRequests] = useState<StuckRequestItem[]>([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalActionSignals = useMemo(
    () =>
      summary.pendingApprovals +
      summary.oldPendingApprovals +
      summary.clinicsWithoutAdmin +
      summary.publicListingIssues +
      summary.stuckRequests,
    [summary]
  );

  const statusTone: DashboardTone =
    totalActionSignals > 0 ? "warning" : "action";

  async function loadDashboard(mode: "initial" | "refresh" = "initial") {
    try {
      if (mode === "initial") {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      setError(null);

      const res = await fetch("/api/super-admin/system-health?limit=5", {
        method: "GET",
        credentials: "include",
      });

      const json = (await res.json().catch(() => ({}))) as HealthResponse;

      if (!res.ok) {
        throw new Error(
          json?.error || `Unable to load dashboard (${res.status})`
        );
      }

      setSummary({
        ...EMPTY_SUMMARY,
        ...(json.summary || {}),
      });

      setClinicsWithoutAdmin(
        Array.isArray(json.lists?.clinicsWithoutAdmin)
          ? json.lists.clinicsWithoutAdmin
          : []
      );

      setPublicListingIssues(
        Array.isArray(json.lists?.publicListingIssues)
          ? json.lists.publicListingIssues
          : []
      );

      setOldPendingApprovals(
        Array.isArray(json.lists?.oldPendingApprovals)
          ? json.lists.oldPendingApprovals
          : []
      );

      setStuckRequests(
        Array.isArray(json.lists?.stuckRequests)
          ? json.lists.stuckRequests
          : []
      );
    } catch (e: any) {
      setError(e?.message || "Unable to load dashboard.");
      setSummary(EMPTY_SUMMARY);
      setClinicsWithoutAdmin([]);
      setPublicListingIssues([]);
      setOldPendingApprovals([]);
      setStuckRequests([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => {
    loadDashboard("initial");
  }, []);

  return (
    <div className="super-admin-dashboard-page">
      {/* Page header */}
      <div className="sad-page-header">
        <div className="sad-kicker">System oversight</div>

        <div className="sad-page-header__main">
          <div className="sad-page-header__copy">
            <h1 className="sad-heading">Dashboard</h1>

            <p className="sad-intro">
              Platform governance workspace for Super Admin oversight. This
              dashboard focuses on clinic onboarding, public-listing safety,
              stuck workflow signals, and audit visibility. Billing, provider
              repair, and advanced analytics are handled outside this dashboard.
            </p>
          </div>

          <button
            className="sad-refresh-button"
            type="button"
            onClick={() => loadDashboard("refresh")}
            disabled={loading || refreshing}
          >
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Status banner */}
      <section className={`sad-status-banner sad-status-banner--${statusTone}`}>
        <div className="sad-status-banner__title">
          {loading
            ? "Loading governance dashboard…"
            : totalActionSignals > 0
              ? "Action signals found"
              : "No current action signals"}
        </div>

        <p className="sad-status-banner__text">
          This dashboard uses the Super Admin system-health endpoint for
          aggregate, non-PHI governance signals. Detailed investigation stays in
          Approvals, Clinics, System Health, and Activity Log.
        </p>
      </section>

      {error ? <div className="sad-error-banner">{error}</div> : null}

      {/* KPI grid */}
      <div className="sad-kpi-grid">
        <DashboardCard
          title="Pending Approvals"
          value={loading ? "—" : formatCount(summary.pendingApprovals)}
          subtitle="Clinic Admin onboarding requests"
          tone={summary.pendingApprovals > 0 ? "action" : "neutral"}
          to="/portal/super-admin/approvals"
        />

        <DashboardCard
          title="Approved Clinics"
          value={loading ? "—" : formatCount(summary.approvedClinics)}
          subtitle="Clinics with approved governance status"
          to="/portal/super-admin/clinics"
        />

        <DashboardCard
          title="Public Listing Issues"
          value={loading ? "—" : formatCount(summary.publicListingIssues)}
          subtitle="Approved clinics missing public eligibility"
          tone={summary.publicListingIssues > 0 ? "warning" : "neutral"}
          to="/portal/super-admin/system-health"
        />

        <DashboardCard
          title="Clinics Without Admin"
          value={loading ? "—" : formatCount(summary.clinicsWithoutAdmin)}
          subtitle="Approved clinics with no active Clinic Admin"
          tone={summary.clinicsWithoutAdmin > 0 ? "warning" : "neutral"}
          to="/portal/super-admin/system-health"
        />

        <DashboardCard
          title="Stuck Requests"
          value={loading ? "—" : formatCount(summary.stuckRequests)}
          subtitle="Patient requests beyond expected action window"
          tone={summary.stuckRequests > 0 ? "warning" : "neutral"}
          to="/portal/super-admin/system-health"
        />

        <DashboardCard
          title="Audit Events Today"
          value={loading ? "—" : formatCount(summary.auditEventsToday)}
          subtitle="Governance events captured today"
          to="/portal/super-admin/activity"
        />
      </div>

      {/* Primary work sections */}
      <div className="sad-two-column-grid sad-two-column-grid--primary">
        <ActionSection
          title="Governance Work Queue"
          description="The most important Super Admin work items requiring attention."
          items={[
            {
              label: "Pending Clinic Admin approvals",
              value: summary.pendingApprovals,
              to: "/portal/super-admin/approvals",
            },
            {
              label: "Old pending approvals",
              value: summary.oldPendingApprovals,
              to: "/portal/super-admin/system-health",
            },
            {
              label: "Clinics without active Clinic Admin",
              value: summary.clinicsWithoutAdmin,
              to: "/portal/super-admin/system-health",
            },
            {
              label: "Approved clinics with public listing issues",
              value: summary.publicListingIssues,
              to: "/portal/super-admin/system-health",
            },
          ]}
          loading={loading}
        />

        <ActionSection
          title="System Safety Signals"
          description="Signals that help maintain controlled onboarding, public listing safeguards, and traceable governance."
          items={[
            {
              label: "Audit events captured today",
              value: summary.auditEventsToday,
              to: "/portal/super-admin/activity",
            },
            {
              label: "Stuck patient-request workflows",
              value: summary.stuckRequests,
              to: "/portal/super-admin/system-health",
            },
            {
              label: "Clinics needing public eligibility repair",
              value: summary.publicListingIssues,
              to: "/portal/super-admin/system-health",
            },
          ]}
          loading={loading}
        />
      </div>

      <div className="sad-two-column-grid sad-two-column-grid--preview">
        <PreviewSection
          title="Old Pending Approvals"
          description="Oldest Clinic Admin approval requests requiring Super Admin review."
          emptyText="No stale pending approvals found."
          items={oldPendingApprovals.map((request) => ({
            key: `approval-${request.accessRequestId}`,
            title:
              request.existingClinicName ||
              request.newClinicName ||
              `Access request #${request.accessRequestId}`,
            meta: [
              `AccessRequest ID: ${request.accessRequestId}`,
              request.clinicAdminAction
                ? `Action: ${request.clinicAdminAction}`
                : null,
              request.submittedAt
                ? `Submitted: ${formatTimestamp(request.submittedAt)}`
                : null,
            ],
          }))}
          loading={loading}
          to="/portal/super-admin/approvals"
        />

        <PreviewSection
          title="Public Listing Issues"
          description="Approved clinics that may not be safe or complete enough for public search."
          emptyText="No public listing issues found."
          items={publicListingIssues.map((clinic) => ({
            key: `public-${clinic.clinicId}`,
            title: clinic.clinicName || `Clinic #${clinic.clinicId}`,
            meta: [
              `Clinic ID: ${clinic.clinicId}`,
              clinic.cityName ? `City: ${clinic.cityName}` : null,
              clinic.missing?.length
                ? `Missing: ${clinic.missing.join(", ")}`
                : null,
            ],
          }))}
          loading={loading}
          to="/portal/super-admin/system-health"
        />

        <PreviewSection
          title="Clinics Without Admin"
          description="Approved clinics that do not currently have active Clinic Admin ownership."
          emptyText="No approved clinics without an active Clinic Admin."
          items={clinicsWithoutAdmin.map((clinic) => ({
            key: `clinic-admin-${clinic.clinicId}`,
            title: clinic.clinicName || `Clinic #${clinic.clinicId}`,
            meta: [
              `Clinic ID: ${clinic.clinicId}`,
              clinic.cityName ? `City: ${clinic.cityName}` : null,
              clinic.approvedAt
                ? `Approved: ${formatTimestamp(clinic.approvedAt)}`
                : null,
            ],
          }))}
          loading={loading}
          to="/portal/super-admin/system-health"
        />

        <PreviewSection
          title="Stuck Requests"
          description="Verified patient request workflows beyond the expected action window. Patient identity is intentionally hidden."
          emptyText="No stuck patient-request workflows found."
          items={stuckRequests.map((request) => ({
            key: `stuck-${request.requestId}`,
            title: request.clinicName || `Clinic #${request.clinicId || "—"}`,
            meta: [
              `Request ID: ${request.requestId}`,
              request.statusName ? `Status: ${request.statusName}` : null,
              request.submittedAt
                ? `Submitted: ${formatTimestamp(request.submittedAt)}`
                : null,
            ],
          }))}
          loading={loading}
          to="/portal/super-admin/system-health"
        />
      </div>

      <Section
        title="Additional Platform Areas"
        description="These areas are managed outside the current dashboard view."
        items={[
          "Billing and subscription management",
          "Provider repair and provider bootstrapping",
          "Advanced patient request analytics",
          "Access and equity dashboards",
          "Geographic coverage maps",
          "Revenue or MRR reporting",
        ]}
        variant="muted"
      />
    </div>
  );
}

/* ---------- Local UI components ---------- */

function DashboardCard({
  title,
  value,
  subtitle,
  tone = "neutral",
  to,
}: {
  title: string;
  value: string;
  subtitle: string;
  tone?: DashboardTone;
  to?: string;
}) {
  const cardClassName = [
    "sad-dashboard-card",
    `sad-dashboard-card--${tone}`,
    to ? "sad-dashboard-card--clickable" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <div className={cardClassName}>
      <div className="sad-dashboard-card__accent" aria-hidden="true" />

      <div className="sad-dashboard-card__title">{title}</div>

      <div className="sad-dashboard-card__value">{value}</div>

      <div className="sad-dashboard-card__subtitle">{subtitle}</div>

      {to ? <div className="sad-dashboard-card__link">View details →</div> : null}
    </div>
  );

  if (!to) return content;

  return (
    <Link to={to} className="sad-card-link">
      {content}
    </Link>
  );
}

function ActionSection({
  title,
  description,
  items,
  loading,
}: {
  title: string;
  description: string;
  items: {
    label: string;
    value: number;
    to: string;
  }[];
  loading: boolean;
}) {
  return (
    <section className="sad-panel">
      <SectionHeader title={title} description={description} />

      <div className="sad-action-list">
        {items.map((item) => {
          const hasAction = item.value > 0;

          return (
            <Link
              key={item.label}
              to={item.to}
              className={`sad-action-row ${
                hasAction ? "sad-action-row--active" : ""
              }`}
            >
              <span className="sad-action-row__label">{item.label}</span>

              <span className="sad-action-row__count">
                {loading ? "—" : formatCount(item.value)}
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

function PreviewSection({
  title,
  description,
  items,
  emptyText,
  loading,
  to,
}: {
  title: string;
  description: string;
  items: {
    key: string;
    title: string;
    meta: Array<string | null>;
  }[];
  emptyText: string;
  loading: boolean;
  to: string;
}) {
  return (
    <section className="sad-panel">
      <div className="sad-panel-header-row">
        <SectionHeader title={title} description={description} noMargin />

        <Link to={to} className="sad-open-link">
          Open →
        </Link>
      </div>

      {loading ? (
        <div className="sad-empty-state">Loading…</div>
      ) : items.length === 0 ? (
        <div className="sad-empty-state">{emptyText}</div>
      ) : (
        <div className="sad-preview-list">
          {items.map((item) => (
            <div key={item.key} className="sad-preview-item">
              <div className="sad-preview-item__title">{item.title}</div>

              <div className="sad-preview-item__meta">
                {item.meta.filter(Boolean).map((meta) => (
                  <span key={meta || ""} className="sad-preview-item__meta-chip">
                    {meta}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function Section({
  title,
  description,
  items,
  variant = "default",
}: {
  title: string;
  description: string;
  items: string[];
  variant?: "default" | "muted";
}) {
  const isMuted = variant === "muted";

  return (
    <section className={`sad-section ${isMuted ? "sad-section--muted" : ""}`}>
      <SectionHeader title={title} description={description} />

      <div className="sad-bullet-list">
        {items.map((item) => (
          <div key={item} className="sad-bullet-item">
            <span className="sad-bullet-item__dot" aria-hidden="true" />
            <span>{item}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function SectionHeader({
  title,
  description,
  noMargin = false,
}: {
  title: string;
  description: string;
  noMargin?: boolean;
}) {
  return (
    <div
      className={`sad-section-header ${
        noMargin ? "sad-section-header--no-margin" : ""
      }`}
    >
      <div className="sad-section-header__title">{title}</div>

      <p className="sad-section-header__description">{description}</p>
    </div>
  );
}

function formatCount(value: number | null | undefined): string {
  const n = Number(value || 0);
  return n.toLocaleString("en-CA");
}

function formatTimestamp(value: string | null | undefined): string {
  if (!value) return "—";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);

  return d.toLocaleString("en-CA", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  });
}